export type CookieCategory = 'essential' | 'analytics' | 'functional';

export type CookiePreferences = {
  analytics: boolean;
  functional: boolean;
};

export type StoredConsent = CookiePreferences & {
  version: string;
  timestamp: number;
};

const STORAGE_KEY = 'it_cookie_consent';
const CONSENT_VERSION = 'v1';

type ConsentListener = (consent: StoredConsent | null) => void;
const listeners = new Set<ConsentListener>();

let settingsOpen = false;
const settingsListeners = new Set<(open: boolean) => void>();

function notify(consent: StoredConsent | null): void {
  for (const fn of listeners) fn(consent);
}

function notifySettings(open: boolean): void {
  settingsOpen = open;
  for (const fn of settingsListeners) fn(open);
}

export function getConsent(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (parsed.version !== CONSENT_VERSION) return null;
    if (typeof parsed.analytics !== 'boolean' || typeof parsed.functional !== 'boolean') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function hasConsent(category: CookieCategory): boolean {
  if (category === 'essential') return true;
  const consent = getConsent();
  if (!consent) return false;
  if (category === 'analytics') return consent.analytics;
  return consent.functional;
}

export function hasStoredConsent(): boolean {
  return getConsent() !== null;
}

export function setConsent(prefs: CookiePreferences): StoredConsent {
  const stored: StoredConsent = {
    ...prefs,
    version: CONSENT_VERSION,
    timestamp: Date.now(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  notify(stored);
  notifySettings(false);
  return stored;
}

export function acceptAllCookies(): StoredConsent {
  return setConsent({ analytics: true, functional: true });
}

export function acceptEssentialOnly(): StoredConsent {
  return setConsent({ analytics: false, functional: false });
}

export function subscribeConsent(listener: ConsentListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function openConsentSettings(): void {
  notifySettings(true);
}

export function closeConsentSettings(): void {
  notifySettings(false);
}

export function isConsentSettingsOpen(): boolean {
  return settingsOpen;
}

export function subscribeConsentSettings(listener: (open: boolean) => void): () => void {
  settingsListeners.add(listener);
  listener(settingsOpen);
  return () => settingsListeners.delete(listener);
}
